import { ChangeDetectionStrategy, Component, computed, effect, input, output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { uniqueId } from '@co-3/ctrl-ui/common';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { PoolObject } from 'app/core/contract/poolObject.model';
import { PortalWidgetDetailsComponent } from 'app/customer-portal/components/portal-widget-details/portal-widget-details.component';
import { InfoTooltipComponent } from 'app/shared/components/info-tooltip/info-tooltip.component';
import { SharedModule } from 'app/shared/shared.module';
import { ValidateIMEI } from 'app/shared/validators/imei.validator';
import { isObjectDetailsComplete } from '../../utils/helper.functions';

@Component({
    imports: [SharedModule, PortalWidgetDetailsComponent, CtrlIconComponent, InfoTooltipComponent],
    selector: 'app-portal-device-details',
    templateUrl: './portal-device-details.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalDeviceDetailsComponent {
    public device = input.required<PoolObject>();
    public lens = input<PoolObject>();

    public submitDetails = output<PoolObject[]>();

    public imeiTooltip =
        'Die IMEI ist eine eindeutige Seriennummer, mit welcher jedes Gerät identifiziert werden kann. Die IMEI kann durch die Eingabe *#06# im Eingabefeld der Telefonnummer abgefragt werden.';

    protected id = uniqueId('device-details-');

    public constructor() {
        this.formGroup.markAllAsTouched();
        effect(() => {
            if (this.isSmartphone()) {
                this.formGroup.get('objectDetail').setValidators([Validators.required, ValidateIMEI]);
            } else {
                this.formGroup.get('objectDetail').setValidators([Validators.required, Validators.minLength(3)]);
            }
            if (this.lens()) {
                this.formGroup.get('lensObjectDetail').setValidators([Validators.required, Validators.minLength(3)]);
            } else {
                this.formGroup.get('lensObjectDetail').clearValidators();
            }
            this.formGroup.get('objectDetail').updateValueAndValidity();
            this.formGroup.get('lensObjectDetail').updateValueAndValidity();
        });
    }

    protected isSmartphone = computed(() => this.device().ObjectType === 'Smartphone');

    protected objectTypeLabel = computed(() => (this.isSmartphone() ? 'IMEI' : 'Seriennummer'));

    protected allComplete = computed(() => this.deviceComplete() && (!this.lens() || this.lensComplete()));

    protected deviceComplete = computed(() => isObjectDetailsComplete(this.device()));

    protected lensComplete = computed(() => isObjectDetailsComplete(this.lens()));

    protected formGroup = new FormGroup({
        objectDetail: new FormControl(''),
        lensObjectDetail: new FormControl(''),
    });

    protected showError(
        controlName: 'objectDetail' | 'lensObjectDetail',
        errorCode?: 'required' | 'notAnIMEI' | 'invalidIMEI' | 'minlength',
    ): boolean {
        const control = this.formGroup.get(controlName);
        const hasError = control.invalid;
        const touched = control.touched;

        if (errorCode == null) {
            return touched && hasError;
        } else {
            return touched && control.hasError(errorCode);
        }
    }

    protected onSubmit(event: SubmitEvent): void {
        const clickedButton = event.submitter as HTMLButtonElement;
        let updatedPoolObjects: PoolObject[];

        if (clickedButton.value === 'device') {
            updatedPoolObjects = [{ ...this.device(), ObjectDetail: this.formGroup.get('objectDetail').value }];
        } else if (clickedButton.value === 'lens') {
            updatedPoolObjects = [{ ...this.lens(), ObjectDetail: this.formGroup.get('lensObjectDetail').value }];
        }
        this.submitDetails.emit(updatedPoolObjects);
    }
}
